import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRightIcon, ChevronRightIcon } from 'lucide-react';
import { Container, Section, Kicker, Pill, Callout } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { SoftGate } from '../components/sections/SoftGate';
import { ArticleFigure } from '../components/insights/ArticleFigure';
import { Seo } from '../components/Seo';
import { articleSchema, breadcrumbSchema } from '../lib/seo';
import { getArticle, ARTICLES } from '../data/insights';
import { SERVICE_LINES } from '../data/services';
import { fetchReaderUnlocked, setReaderUnlocked } from '../lib/leads';
import { usePortalAuth } from '../context/PortalAuth';
import { NotFound } from './NotFound';
export function ArticlePage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const article = getArticle(slug ?? '');
  const {
    user
  } = usePortalAuth();
  const reduced = useReducedMotion();
  const [unlocked, setUnlocked] = React.useState(false);
  React.useEffect(() => {
    let active = true;
    void fetchReaderUnlocked().then(value => {
      if (active) setUnlocked(value);
    });
    return () => {
      active = false;
    };
  }, [slug]);
  if (!article) return <NotFound data-id="element-2076" data-mp="2hw1zdb065ghh" />;
  const gateApplies = article.gated && !unlocked && !user;
  const teaser = article.body.slice(0, article.teaserCut);
  const remainder = article.body.slice(article.teaserCut);
  const related = ARTICLES.filter(item => item.slug !== article.slug).slice(0, 3);
  const lineName = SERVICE_LINES.find(line => line.id === article.serviceLine)?.name;
  const wordCount = article.body.reduce((total, section) => total + section.paragraphs.reduce((count, paragraph) => count + paragraph.split(/\s+/).length, 0), 0);
  const onUnlock = () => {
    setReaderUnlocked();
    setUnlocked(true);
  };
  return <>
      <Seo title={article.title} description={article.excerpt} path={`/insights/${article.slug}`} image={article.heroImage} type="article" article={{
      publishedAt: article.publishedAt,
      author: article.author.name,
      section: lineName,
      tags: article.sectors
    }} jsonLd={[articleSchema({
      title: article.title,
      description: article.excerpt,
      path: `/insights/${article.slug}`,
      image: article.heroImage,
      publishedAt: article.publishedAt,
      author: article.author,
      section: lineName,
      about: article.sectors,
      wordCount,
      readTime: article.readTime,
      summary: article.summaryBlock
    }), breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Insights',
      path: '/insights'
    }, {
      name: article.title,
      path: `/insights/${article.slug}`
    }])]} data-id="element-2077" data-mp="16g3m0uxyk8ag" />

      <section className="surface-dark" data-id="element-2078" data-mp="21blhdohxps53">
        <Container className="py-12 sm:py-16" data-id="element-2079" data-mp="21aju8lnn9f70">
          <nav aria-label="Breadcrumb" className="mb-8" data-id="element-2080" data-mp="1wcs1jpf6dp1r">
            <ol className="flex flex-wrap items-center gap-2 text-small text-white/55" data-id="element-2081" data-mp="1wda7xevjxjne">
              <li data-id="element-2082" data-mp="1rkqt6el4oih0"><Link to="/" className="hover:text-white" data-id="element-2083" data-mp="1rkqrma5hcaew">Home</Link></li>
              <li aria-hidden="true" data-id="element-2084" data-mp="1mngt85044oex"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-2085" data-mp="1mm5rsbu53sqz" /></li>
              <li data-id="element-2086" data-mp="1hnm7efv72p0q"><Link to="/insights" className="hover:text-white" data-id="element-2087" data-mp="1hodpcnvvco8j">Insights</Link></li>
              <li aria-hidden="true" data-id="element-2088" data-mp="1dmadvz979jn9"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-2089" data-mp="1dlr270a57rho" /></li>
              <li aria-current="page" className="truncate text-white/80" data-id="element-2090" data-mp="1don4f0t0x2bx">{article.title}</li>
            </ol>
          </nav>
          <Kicker data-id="element-2091" data-mp="13ocp9anfhku4">{lineName}</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[30px] leading-tight text-white sm:text-[42px]" data-id="element-2092" data-mp="13peb8a5zfrqm">
            {article.title}
          </h1>
          <p className="mt-6 text-small text-white/55" data-id="element-2093" data-mp="2y2454rwcp0y0">
            {article.author.name}, {article.author.role} ·{' '}
            {new Date(article.publishedAt).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          })}{' '}
            · {article.readTime} minute read
          </p>
        </Container>
      </section>

      <Container className="py-10 sm:py-14" data-id="element-2094" data-mp="1qpmmagwooic0">
        <img src={article.heroImage} alt={article.heroAlt} className="aspect-[16/7] w-full rounded-md object-cover" data-id="element-2095" data-mp="1qobk2livzin0" />

        <div className="mt-8 flex flex-wrap gap-2" data-id="element-2096" data-mp="1gr6vyg5hdd3k">
          {article.sectors.map(sector => <Pill key={sector} data-id="element-2097" data-mp="130ahdt8e2wfi">{sector}</Pill>)}
        </div>

        <div className="mt-10 max-w-prose" data-id="element-2098" data-mp="12xxrmtwe3hrw">
          <Callout data-id="element-2099" data-mp="17yc02d1wktqg">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-2100" data-mp="17zwu81kb2ans">In short</p>
            <p className="mt-2 text-[15px] text-body" data-id="element-2101" data-mp="t35rpjfc4ity">{article.summaryBlock}</p>
          </Callout>
        </div>

        <article className="mt-10 max-w-prose" data-id="element-2102" data-mp="y05zpzvqvvnp">
          {teaser.map((section, index) => <section key={index} data-id="element-2103" data-mp="efnuf643qqhk" data-mpi={index}>
              {section.heading && <h2 className="mb-4 mt-10 font-display text-[24px] leading-snug text-ink first:mt-0" data-id="element-2104" data-mp="aelvyri0eign">
                  {section.heading}
                </h2>}
              {section.paragraphs.map((paragraph, pIndex) => <p key={pIndex} className="mb-5 text-[17px] leading-[1.7] text-body" data-id="element-2105" data-mp="jgbmlv42hddd" data-mpi={pIndex}>
                  {paragraph}
                </p>)}
            </section>)}

          {/* The remaining content stays in the served markup for indexing. The gate is a client side reveal only. */}
          <div className={gateApplies ? 'pointer-events-none max-h-64 select-none overflow-hidden' : ''} aria-hidden={gateApplies} data-id="element-2106" data-mp="175xncn0380nh">
            {remainder.map((section, index) => <motion.section key={index} initial={reduced || gateApplies ? false : {
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.35,
            ease: 'easeOut',
            delay: index * 0.03
          }} data-id="element-2107" data-mp="1h28znczogqhx" data-mpi={index}>
                {section.heading && <h2 className="mb-4 mt-10 font-display text-[24px] leading-snug text-ink" data-id="element-2108" data-mp="1t4a9fs2ve3m0">{section.heading}</h2>}
                {section.paragraphs.map((paragraph, pIndex) => <p key={pIndex} className="mb-5 text-[17px] leading-[1.7] text-body" data-id="element-2109" data-mp="22u3lsvau6mrb" data-mpi={pIndex}>
                    {paragraph}
                  </p>)}
              </motion.section>)}
          </div>

          {gateApplies && <SoftGate article={article} onUnlock={onUnlock} data-id="element-2110" data-mp="2bui5jq85jtsp" />}

          {!gateApplies && article.figure && <ArticleFigure figure={article.figure} data-id="element-2111" data-mp="2gu4moar3pu6s" />}

          {!gateApplies && article.sources.length > 0 && <section aria-labelledby="article-sources" className="mt-12 border-t border-line pt-8" data-id="element-2112" data-mp="hi25j75y9bwa">
              <h2 id="article-sources" className="font-display text-[20px] text-ink" data-id="element-2113" data-mp="mgmugafr495f">
                Sources
              </h2>
              <ol className="mt-5 space-y-4" data-id="element-2114" data-mp="eyf87k1jcneg">
                {article.sources.map(source => <li key={source.url} className="text-[15px] leading-relaxed text-body" data-id="element-2115" data-mp="a3j61r2l4jnm">
                    {source.title}.{' '}
                    <a href={source.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-royal underline decoration-royal/40 underline-offset-4 transition-colors duration-150 hover:text-ink" data-id="element-2116" data-mp="7ohygfldwdg">
                      View source
                      <ArrowUpRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2117" data-mp="nytykxtcwsot" />
                      <span className="sr-only" data-id="element-2118" data-mp="j00hywirwlkf">(opens in a new tab)</span>
                    </a>
                  </li>)}
              </ol>
            </section>}
        </article>

        {!gateApplies && <div className="mt-12 max-w-prose rounded-md border border-line bg-mist p-6 sm:p-8" data-id="element-2119" data-mp="3tck680kp63wm">
            <h2 className="font-display text-[22px] text-ink" data-id="element-2120" data-mp="25pvfb06xl2c">Working on something similar</h2>
            <p className="mt-3 text-[15px] text-body" data-id="element-2121" data-mp="3kbvsl6z7u5k9">
              A discovery call lasts thirty minutes and costs nothing. Tell us the decision in front of you.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-id="element-2122" data-mp="3of0up8fdw58d">
              <Button to="/contact" data-id="element-2123" data-mp="fxxnwqkfypv8">Book a discovery call</Button>
              <Button to="/assessment" variant="secondary" data-id="element-2124" data-mp="kx0gvcn5d5mu">Take the assessment</Button>
            </div>
          </div>}
      </Container>

      <Section tone="mist" aria-labelledby="related-articles" data-id="element-2125" data-mp="74u3fybdpcr3">
        <h2 id="related-articles" className="font-display text-[26px] text-ink" data-id="element-2126" data-mp="b5w6yrcbwayv">
          Related reading
        </h2>
        <div className="mt-8 grid gap-8 sm:grid-cols-3" data-id="element-2127" data-mp="yup3aowv4ku0">
          {related.map(item => <article key={item.slug} data-id="element-2128" data-mp="13nryt2l7a4cu">
              <p className="text-small uppercase tracking-[0.12em] text-gold" data-id="element-2129" data-mp="13otdri5c7azu">{item.sectors[0]}</p>
              <h3 className="mt-3 font-display text-[19px] leading-snug text-ink" data-id="element-2130" data-mp="2ob14r07ym664">
                <Link to={`/insights/${item.slug}`} className="underline-offset-4 hover:underline" data-id="element-2131" data-mp="2jc81s1qatxk7">
                  {item.title}
                </Link>
              </h3>
              <p className="mt-3 text-[15px] text-body" data-id="element-2132" data-mp="2t5f23866mndf">{item.excerpt}</p>
            </article>)}
        </div>
      </Section>
    </>;
}